export type SupportSourceRole = 'Agent' | 'Client'

export interface SupportTicket {
    MessageIds?: unknown
    TicketType?: unknown
    TicketOpen?: unknown
    TopicKey?: unknown
    SourceRole?: unknown
}

export interface SupportTicketPresentation {
    topic: string
    source: SupportSourceRole | undefined
    status: 'Open' | 'Resolved'
}

const legacyLabels: Record<string, string> = {
    viewing: 'Viewings',
    offer: 'Offers',
    advertise: 'Advertising',
    profile: 'Profile and account',
    units: 'Units and portfolio',
    other: 'General support'
}

const agentTopics: Record<string, string> = {
    'listings-units': 'Listings and units',
    viewings: 'Viewings',
    offers: 'Offers',
    advertising: 'Advertising',
    'profile-account': 'Profile and account',
    'general-support': 'General support'
}

const clientTopics: Record<string, string> = {
    'portfolio-units': 'Portfolio and units',
    services: 'Services',
    'service-orders': 'Service orders',
    'finding-tenant': 'Finding tenant activity',
    'profile-account': 'Profile and account',
    'general-support': 'General support'
}

const allTopicKeys: Record<string, string> = { ...agentTopics, ...clientTopics }

export function getSupportSourceRole(value: unknown): SupportSourceRole | undefined {
    return value === 'Agent' || value === 'Client' ? value : undefined
}

export function getLegacySupportTicketLabel(ticketType: unknown): string {
    return typeof ticketType === 'string' ? legacyLabels[ticketType] ?? 'General support' : 'General support'
}

export function getSupportTopicLabel(topicKey: unknown, sourceRole: unknown, ticketType?: unknown): string {
    const source = getSupportSourceRole(sourceRole)
    if (typeof topicKey === 'string') {
        const topics = source === 'Agent' ? agentTopics : source === 'Client' ? clientTopics : allTopicKeys
        return topics[topicKey] ?? 'General support'
    }

    return getLegacySupportTicketLabel(ticketType)
}

export function getLatestSupportTicket(tickets: unknown): SupportTicket | undefined {
    if (!Array.isArray(tickets)) return undefined
    for (let index = tickets.length - 1; index >= 0; index -= 1) {
        const ticket = tickets[index]
        if (ticket != null && typeof ticket === 'object' && !Array.isArray(ticket)) return ticket as SupportTicket
    }
    return undefined
}

export function getSupportTicketPresentation(ticket: unknown): SupportTicketPresentation | undefined {
    if (ticket == null || typeof ticket !== 'object' || Array.isArray(ticket)) return undefined
    const value = ticket as SupportTicket
    return {
        topic: getSupportTopicLabel(value.TopicKey, value.SourceRole, value.TicketType),
        source: getSupportSourceRole(value.SourceRole),
        status: value.TicketOpen === true ? 'Open' : 'Resolved'
    }
}

export type SupportMarker = { kind: 'open', topic: string } | { kind: 'close' }

/** Recognizes persisted legacy markers and the TopicKey forms used by support clients. */
export function getSupportMarker(text: unknown): SupportMarker | undefined {
    if (text === 'type:close-issue' || text === 'type:close-issue-resolved' || /^type:close-issue-(?:Agent|Client)[:-].+$/.test(String(text))) return { kind: 'close' }
    if (typeof text !== 'string') return undefined

    const topicPrefix = 'type:open-issue-topic-'
    if (text.startsWith(topicPrefix)) return { kind: 'open', topic: getSupportTopicLabel(text.slice(topicPrefix.length), undefined) }

    const legacyPrefix = 'type:open-issue-'
    if (text.startsWith(legacyPrefix)) {
        const suffix = text.slice(legacyPrefix.length)
        const roleMatch = /^(Agent|Client)[:-](.+)$/.exec(suffix) ?? /^(.+?)[-:](Agent|Client)$/.exec(suffix)
        if (roleMatch != null) {
            const [, first, second] = roleMatch
            const [role, topicKey] = first === 'Agent' || first === 'Client' ? [first, second] : [second, first]
            return { kind: 'open', topic: getSupportTopicLabel(topicKey, role) }
        }
        if (legacyLabels[suffix] != null) return { kind: 'open', topic: getLegacySupportTicketLabel(suffix) }
        return { kind: 'open', topic: getSupportTopicLabel(suffix, undefined, suffix) }
    }

    const newFormat = /^type:open-issue(?:-topic)?:?(Agent|Client):(.+)$/.exec(text)
    if (newFormat != null) return { kind: 'open', topic: getSupportTopicLabel(newFormat[2], newFormat[1]) }
    return undefined
}
